export const CHECKOUT_URL = 'https://treatment-en.impultienda.ar/checkout/';

export const IMAGES = {
  mainMockup:
    'https://cdn.magicpatterns.com/uploads/2vqmtisE4wRYRqGLuRaBrj/TREATMENT-EN.png',
  bonus1:
    'https://cdn.magicpatterns.com/uploads/637rqSJhJcsvCBy4w7AAUT/BONSU_1_TREATMENT-EN.png',
  bonus2:
    'https://cdn.magicpatterns.com/uploads/pj9vsgXScXYan11tFfm7vJ/BONUS_2_TREATMENT-EN.png',
  bonus3:
    'https://cdn.magicpatterns.com/uploads/nDwZjNYRApYUyRR7UdXvTp/BONUS_3_TREATMENT-EN.png',
  bonus4:
    'https://cdn.magicpatterns.com/uploads/7xc8eKzooVhqtsi1f7auuN/BONUS_4_TREATMENT-EN.png',
  bonus5:
    'https://cdn.magicpatterns.com/uploads/qLeH3JUh7W6Zzs8JagnDNz/BONUS_5_TREATMENT-EN.png',
  author:
    'https://cdn.magicpatterns.com/patterns/generated-images/ef7d5253-80fb-4695-800e-b05c6017ed91.jpg',
};

export const scatteredItems = [
  'Case assessment',
  'Records',
  'Treatment objectives',
  'Digital planning',
  'Tooth movements',
  'Attachments',
  'IPR',
  'Installation',
  'Follow-up',
  'Progress monitoring',
  'Refinement',
  'Finishing',
];

export const workflowStages = [
  'Assess',
  'Plan',
  'Prepare',
  'Control',
  'Refine',
  'Finish',
];

export const beforeItems = [
  'You search through different resources before reviewing a case.',
  'You aren’t always sure what to review first.',
  'Digital treatment planning can become an improvised process.',
  'You have too many variables to keep track of.',
  'You go back through your notes before an installation.',
  'Follow-up depends heavily on memory.',
  'Every case ends up being organized differently.',
];

export const afterItems = [
  'You organize each case using the same visual workflow.',
  'You know what information to review at each stage.',
  'You structure the treatment objectives before moving forward.',
  'You document key review points.',
  'You have dedicated planning maps and worksheets.',
  'You keep the case timeline organized.',
  'You can quickly identify the current stage and next review point.',
];

export const familiarItems = [
  {
    emoji: '😰',
    text: 'You have an aligner case in front of you and want a clearer structure for what to review before getting started.',
  },
  {
    emoji: '⏱️',
    text: 'You spend too much time going back through courses, notes, PDFs, and different resources.',
  },
  {
    emoji: '🤔',
    text: 'Before approving a digital treatment plan, you want to make sure you’ve reviewed the important elements of the case.',
  },
  {
    emoji: '📋',
    text: 'You don’t have one practical system for organizing the case from assessment through finishing.',
  },
  {
    emoji: '🔄',
    text: 'When a case requires reassessment or refinement, you have to reconstruct the case history.',
  },
  {
    emoji: '😓',
    text: 'You know you need more structure in your aligner workflow, but you don’t want to spend hours creating your own protocols and templates.',
  },
];

export const questions = [
  'What should I review?',
  'In what order?',
  'What should I document?',
  'What should I verify before moving forward?',
  'Where is this case in the treatment workflow?',
  'What should I review next?',
];

export type Volume = {
  number: string;
  title: string;
  lead: string;
  description: string;
  items: string[];
  note?: string;
};

export const volumes: Volume[] = [
  {
    number: 'Volume I',
    title: 'The Case Blueprint',
    lead: 'Build the map of your case before treatment begins.',
    description:
      'Organize the essential information from the initial evaluation and turn scattered case information into a structured treatment overview.',
    items: [
      'Case Blueprint',
      'Case Assessment Framework',
      'Initial Evaluation Checklist',
      'Records Checklist',
      'Patient Information Sheet',
      'Treatment Objectives Worksheet',
      'Pre-Planning Review',
      'Case Organization Map',
    ],
  },
  {
    number: 'Volume II',
    title: 'The Treatment Planning Blueprint',
    lead: 'Turn digital treatment planning into a structured review process.',
    description:
      'Organize the key elements to review when evaluating a proposed aligner treatment plan.',
    items: [
      'Digital Planning Framework',
      'Planning Checklist',
      'Treatment Objectives Map',
      'Movement Review Framework',
      'Case Review Map',
      'Planning Worksheet',
      'Treatment Planning Tracker',
      'Pre-Approval Review Checklist',
    ],
  },
  {
    number: 'Volume III',
    title: 'The Installation Blueprint',
    lead: 'Organize everything that needs to happen before aligner delivery.',
    description:
      'Create a consistent preparation workflow for the appointment instead of relying on memory.',
    items: [
      'Pre-Installation Checklist',
      'Installation Workflow',
      'Patient Preparation Sheet',
      'Delivery Checklist',
      'Adaptation Review',
      'Documentation Checklist',
      'Installation Quick Reference',
      'Appointment Preparation Map',
    ],
  },
  {
    number: 'Volume IV',
    title: 'The Attachment & IPR Blueprint',
    lead: 'Structure the procedures surrounding attachments and IPR.',
    description:
      'Organize preparation, documentation, and review points associated with these procedures within the overall treatment workflow.',
    items: [
      'Attachment Workflow',
      'Attachment Checklist',
      'Material Review Sheet',
      'IPR Planning Checklist',
      'IPR Documentation Sheet',
      'Pre-Procedure Checklist',
      'Post-Procedure Review',
      'Procedure Quick Reference Cards',
    ],
    note: 'These materials are educational and organizational tools. They do not replace professional training, clinical judgment, manufacturer instructions, or applicable laws and regulations.',
  },
  {
    number: 'Volume V',
    title: 'The Case Control Blueprint',
    lead: 'Keep your entire case organized throughout treatment.',
    description:
      'Turn every follow-up appointment into a structured review and documentation process.',
    items: [
      'Patient Progress Tracker',
      'Follow-Up Checklist',
      'Control Visit Sheet',
      'Case Timeline',
      'Progress Monitoring Sheet',
      'Documentation Tracker',
      'Case Review Framework',
      'Review Questions',
    ],
  },
  {
    number: 'Volume VI',
    title: 'The Refinement & Finishing Blueprint',
    lead: 'Organize the final stages of your aligner cases.',
    description:
      'Structure reassessment, refinement planning, finishing, and treatment completion.',
    items: [
      'Refinement Review Framework',
      'Case Reassessment Sheet',
      'Refinement Checklist',
      'Finishing Checklist',
      'Final Documentation Sheet',
      'Treatment Completion Review',
      'Retention Planning Checklist',
      'Final Case Summary',
    ],
  },
];

export const immediateAccess = [
  {
    emoji: '🧭',
    title: 'Planning Maps',
    text: 'Visual frameworks that help organize what to review at different stages of an aligner case.',
  },
  {
    emoji: '📋',
    title: 'Professional Checklists',
    text: 'Structured checklists for the major stages of the aligner workflow.',
  },
  {
    emoji: '📊',
    title: 'Case Trackers',
    text: 'Track progress, appointments, documentation, and review points in one organized system.',
  },
  {
    emoji: '📝',
    title: 'Planning Templates',
    text: 'Turn scattered case information into structured worksheets you can actually use.',
  },
  {
    emoji: '⚡',
    title: 'Quick Reference Tools',
    text: 'Find the resource you need without searching through an entire manual.',
  },
];

export type Bonus = {
  label: string;
  title: string;
  value: string;
  description: string;
  items: string[];
  footer?: string;
  image: string;
};

export const bonuses: Bonus[] = [
  {
    label: 'Bonus #1',
    title: 'Aligner Case Quick Reference Cards™',
    value: '$37',
    description:
      'A collection of visual quick-reference cards covering the key stages of the aligner workflow.',
    items: [
      'Assessment Card',
      'Planning Card',
      'Installation Card',
      'Procedure Card',
      'Control Card',
      'Refinement Card',
      'Finishing Card',
    ],
    footer: 'Designed to help you find the right framework quickly.',
    image: IMAGES.bonus1,
  },
  {
    label: 'Bonus #2',
    title: 'Case Control Tracker™',
    value: '$37',
    description:
      'A complete collection of tracking sheets designed to keep each case organized from initial assessment through treatment completion.',
    items: [
      'Case Timeline',
      'Progress Tracker',
      'Follow-Up Tracker',
      'Documentation Tracker',
      'Case Review Sheet',
    ],
    image: IMAGES.bonus2,
  },
  {
    label: 'Bonus #3',
    title: 'The 15-Minute Case Setup™',
    value: '$29',
    description:
      'A visual organization protocol designed to help you structure the essential information of an aligner case before beginning your detailed review.',
    items: [
      '15-Minute Setup Map',
      'Case Capture Sheet',
      'Review Sequence',
      'Next-Step Worksheet',
      'Quick Organization Checklist',
    ],
    image: IMAGES.bonus3,
  },
  {
    label: 'Bonus #4',
    title: 'Refinement Review Kit™',
    value: '$37',
    description:
      'A collection of tools designed to structure the review of cases that require reassessment and potential refinement planning.',
    items: [
      'Case Review Sheet',
      'Refinement Checklist',
      'Documentation Sheet',
      'Review Questions',
      'Refinement Planning Framework',
    ],
    image: IMAGES.bonus4,
  },
  {
    label: 'Bonus #5',
    title: 'Aligner Clinical Desk™',
    value: '$27',
    description:
      'A visual toolkit designed to keep your most-used ALIGNER TREATMENT BLUEPRINT™ resources within easy reach.',
    items: [
      'Treatment Journey Poster',
      'Master Workflow',
      'Case Control Map',
      'Planning Card',
      'Installation Card',
      'Control Card',
      'Refinement Card',
      'Finishing Card',
    ],
    image: IMAGES.bonus5,
  },
];

export const testimonials = [
  {
    name: 'Dr. Sofia R.',
    country: 'Mexico',
    flag: '🇲🇽',
    quote:
      'Before every treatment plan, I was going back through different resources. Having everything organized by stage gives me a much clearer review process.',
    photo:
      'https://cdn.magicpatterns.com/patterns/generated-images/cd51ba1a-ffb9-4f0b-8b10-15b814bfb0a0.jpg',
  },
  {
    name: 'Dr. Andrew G.',
    country: 'Colombia',
    flag: '🇨🇴',
    quote:
      'The treatment planning map is the tool I use the most. It gives me a structured way to review the case before moving forward.',
    photo:
      'https://cdn.magicpatterns.com/patterns/generated-images/05f06ace-2efd-4eba-8fac-ca79e50f2484.jpg',
  },
  {
    name: 'Dr. Lucia F.',
    country: 'Spain',
    flag: '🇪🇸',
    quote:
      'The case tracker changed how I document follow-ups. I can see the entire treatment history in one place.',
    photo:
      'https://cdn.magicpatterns.com/patterns/generated-images/0cb81d88-49e1-44c9-8970-b038c5970224.jpg',
  },
  {
    name: 'Dr. Martin S.',
    country: 'Chile',
    flag: '🇨🇱',
    quote:
      'What I like most is that it isn’t another long course. It’s a system I can open when I actually have a case in front of me.',
    photo:
      'https://cdn.magicpatterns.com/patterns/generated-images/8f900089-1557-4fa0-901c-8fc4d280f072.jpg',
  },
  {
    name: 'Dr. Camila D.',
    country: 'Panama',
    flag: '🇵🇦',
    quote:
      'Our team now has a consistent structure for organizing our aligner cases.',
    photo:
      'https://cdn.magicpatterns.com/patterns/generated-images/827bc281-9d32-422a-8b95-23755d68fbe5.jpg',
  },
];

export const faqs = [
  {
    q: 'How will I receive the materials?',
    a: 'Immediately after your purchase, you’ll receive instructions to access and download all six volumes, tools, and bonuses in digital format.',
  },
  {
    q: 'Is this a course?',
    a: 'No. ALIGNER TREATMENT BLUEPRINT™ is a digital case organization and planning system containing frameworks, checklists, maps, trackers, worksheets, and quick-reference tools.',
  },
  {
    q: 'Is it suitable for general dentists?',
    a: 'It is designed for dental professionals who work with aligner treatments and want to structure their case-planning workflow. It does not replace professional education or clinical judgment.',
  },
  {
    q: 'Do I need specific aligner software?',
    a: 'No. The Blueprint is designed as an organization and review system and is not dependent on one specific planning platform.',
  },
  {
    q: 'Does the offer include all 6 volumes?',
    a: 'Yes. Your purchase includes all six premium volumes of ALIGNER TREATMENT BLUEPRINT™.',
  },
  {
    q: 'Are the files downloadable?',
    a: 'Yes. The digital materials can be downloaded and accessed whenever you need them.',
  },
  {
    q: 'Is this a one-time payment?',
    a: 'Yes. You pay just $27 once. Currency conversion is automatic, so you are charged in your local currency.',
  },
  {
    q: 'Do I get lifetime access?',
    a: 'Yes, subject to the access terms of the offer.',
  },
  {
    q: 'Can I use it in any country?',
    a: 'The system is designed as an educational and organizational resource. Each professional should adapt its use to their training, local regulations, practice protocols, and applicable manufacturer instructions.',
  },
  {
    q: 'What if I don’t like it?',
    a: 'You have a 7-day guarantee according to the terms of the offer.',
  },
];

export const purchaseNotifications = [
  { name: 'Dr. Valentina M.', country: 'Argentina', flag: '🇦🇷' },
  { name: 'Dr. Ricardo P.', country: 'Mexico', flag: '🇲🇽' },
  { name: 'Dr. Daniela C.', country: 'Colombia', flag: '🇨🇴' },
  { name: 'Dr. Javier L.', country: 'Spain', flag: '🇪🇸' },
  { name: 'Dr. Paula T.', country: 'Chile', flag: '🇨🇱' },
  { name: 'Dr. Mateo S.', country: 'Peru', flag: '🇵🇪' },
  { name: 'Dr. Gabriela R.', country: 'Ecuador', flag: '🇪🇨' },
  { name: 'Dr. Andrés V.', country: 'Uruguay', flag: '🇺🇾' },
  { name: 'Dr. Carolina B.', country: 'Costa Rica', flag: '🇨🇷' },
  { name: 'Dr. Fernando D.', country: 'Guatemala', flag: '🇬🇹' },
  { name: 'Dr. Lucía A.', country: 'Dominican Republic', flag: '🇩🇴' },
  { name: 'Dr. Tomás H.', country: 'Panama', flag: '🇵🇦' },
  { name: 'Dr. Isabela N.', country: 'Bolivia', flag: '🇧🇴' },
  { name: 'Dr. Emilio Q.', country: 'Paraguay', flag: '🇵🇾' },
  { name: 'Dr. Rocío G.', country: 'Venezuela', flag: '🇻🇪' },
];
